import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Select, Empty, Row, Col } from 'antd';
import ChartCard from './ChartCard';
import { apiClient, API_ENDPOINTS } from '../../utils/api';
import styled, { keyframes } from 'styled-components';

const COLORS = {
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  success: '#10B981',
  failed: '#F87171',
  pending: '#FBBF24',
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 300px;
  animation: ${fadeIn} 0.6s ease-out;
`;

const SelectContainer = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: flex-end;
`;

const StyledSelect = styled(Select)`
  width: 150px;
  
  .ant-select-selector {
    background: #161B22 !important;
    border: 1px solid #1f2a37 !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-select-selection-item {
    color: ${COLORS.textPrimary} !important;
  }
`;

const StatsRow = styled(Row)`
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: linear-gradient(135deg, #0D1117 0%, #161B22 100%);
  border: 1px solid #1f2a37;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  transition: all 0.3s ease;
  animation: ${slideIn} 0.6s ease-out;
  animation-fill-mode: both;
  
  &:nth-child(1) { animation-delay: 0.1s; }
  &:nth-child(2) { animation-delay: 0.2s; }
  &:nth-child(3) { animation-delay: 0.3s; }
  &:nth-child(4) { animation-delay: 0.4s; }
  
  &:hover {
    transform: translateY(-4px);
    border-color: rgba(34, 211, 238, 0.5);
    box-shadow: 0 8px 24px rgba(34, 211, 238, 0.2);
  }
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${COLORS.textPrimary};
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: ${COLORS.textSecondary};
`;


interface WebhookTrendData {
  date: string;
  SUCCESS: number;
  FAILED: number;
  PENDING: number;
}

interface WebhookStatsData {
  trends: WebhookTrendData[];
  summary: {
    total: number;
    success: number;
    failed: number;
    pending: number;
    successRate: number;
  };
}

interface WebhookStatsChartProps {
  isAdmin?: boolean;
}

const WebhookStatsChart: React.FC<WebhookStatsChartProps> = ({ isAdmin = true }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<WebhookStatsData | null>(null);
  const [days, setDays] = useState(30);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const endpoint = isAdmin
        ? `${API_ENDPOINTS.ADMIN_GET_WEBHOOK_STATS}?days=${days}`
        : `${API_ENDPOINTS.MERCHANT_GET_WEBHOOK_STATS}?days=${days}`;
      const response = await apiClient.get(endpoint);
      if (response.data?.status === 'success' && response.data?.data) {
        setData(response.data.data);
        console.log('Webhook Stats Data:', response.data.data);
      } else {
        console.warn('Webhook Stats: Unexpected response format', response.data);
      }
    } catch (error: any) {
      console.error('Error fetching webhook stats:', error);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days, isAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: '#161B22',
          border: '1px solid #1f2a37',
          borderRadius: '8px',
          padding: '12px',
          color: COLORS.textPrimary,
        }}>
          <p style={{ margin: 0, marginBottom: '8px', fontWeight: 600 }}>
            {payload[0].payload.date ? formatDate(payload[0].payload.date) : ''}
          </p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ margin: 0, color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <ChartCard
      title="Webhook Delivery Statistics"
      extra={
        <SelectContainer>
          <StyledSelect
            value={days}
            onChange={(value) => setDays(value as number)}
            style={{ width: 150 }}
          >
            <Select.Option value={7}>Last 7 days</Select.Option>
            <Select.Option value={30}>Last 30 days</Select.Option>
            <Select.Option value={90}>Last 90 days</Select.Option>
          </StyledSelect>
        </SelectContainer>
      }
      loading={loading}
    >
      {!loading && (!data || data.trends.length === 0 || (
        data.summary?.total === 0 && 
        data.trends.every(item => item.SUCCESS === 0 && item.FAILED === 0 && item.PENDING === 0)
      )) ? (
        <Empty 
          description={
            <div>
              <p style={{ marginBottom: '8px', color: COLORS.textPrimary }}>
                No webhook statistics available
              </p>
              <p style={{ fontSize: '12px', color: COLORS.textSecondary }}>
                Data will appear here once webhooks are sent to merchants
              </p>
            </div>
          }
        />
      ) : (
        <>
          {data?.summary && (
            <StatsRow gutter={16}>
              <Col xs={12} sm={6}>
                <StatCard>
                  <StatValue>{data.summary.total}</StatValue>
                  <StatLabel>Total Webhooks</StatLabel>
                </StatCard>
              </Col>
              <Col xs={12} sm={6}>
                <StatCard>
                  <StatValue style={{ color: COLORS.success }}>
                    {data.summary.success}
                  </StatValue>
                  <StatLabel>Successful</StatLabel>
                </StatCard>
              </Col>
              <Col xs={12} sm={6}>
                <StatCard>
                  <StatValue style={{ color: COLORS.failed }}>
                    {data.summary.failed}
                  </StatValue>
                  <StatLabel>Failed</StatLabel>
                </StatCard>
              </Col>
              <Col xs={12} sm={6}>
                <StatCard>
                  <StatValue style={{ color: COLORS.success }}>
                    {data.summary.successRate.toFixed(1)}%
                  </StatValue>
                  <StatLabel>Success Rate</StatLabel>
                </StatCard>
              </Col>
            </StatsRow>
          )}
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.trends || []} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
                <defs>
                  <linearGradient id="successGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.success} stopOpacity={1}/>
                    <stop offset="100%" stopColor={COLORS.success} stopOpacity={0.7}/>
                  </linearGradient>
                  <linearGradient id="failedGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.failed} stopOpacity={1}/>
                    <stop offset="100%" stopColor={COLORS.failed} stopOpacity={0.7}/>
                  </linearGradient>
                  <linearGradient id="pendingGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLORS.pending} stopOpacity={1}/>
                    <stop offset="100%" stopColor={COLORS.pending} stopOpacity={0.7}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2a37" opacity={0.5} />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  stroke={COLORS.textSecondary}
                  style={{ fontSize: '12px' }}
                  tick={{ fill: COLORS.textSecondary }}
                />
                <YAxis stroke={COLORS.textSecondary} style={{ fontSize: '12px' }} tick={{ fill: COLORS.textSecondary }} />
                <Tooltip 
                  content={<CustomTooltip />}
                  cursor={{ fill: 'rgba(34, 211, 238, 0.1)' }}
                />
                <Legend
                  wrapperStyle={{ color: COLORS.textPrimary, paddingTop: '20px' }}
                  iconSize={16}
                />
                <Bar 
                  dataKey="SUCCESS" 
                  fill="url(#successGradient)" 
                  name="Success"
                  radius={[8, 8, 0, 0]}
                  animationDuration={1000}
                  animationEasing="ease-out"
                />
                <Bar 
                  dataKey="FAILED" 
                  fill="url(#failedGradient)" 
                  name="Failed"
                  radius={[8, 8, 0, 0]}
                  animationDuration={1000}
                  animationEasing="ease-out"
                />
                <Bar 
                  dataKey="PENDING" 
                  fill="url(#pendingGradient)" 
                  name="Pending"
                  radius={[8, 8, 0, 0]}
                  animationDuration={1000}
                  animationEasing="ease-out"
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </>
      )}
    </ChartCard>
  );
};

export default WebhookStatsChart;

